import { Status as NovaStatus } from '@fme-nova/components'
import React from 'react'

import { createComponentWrapperWithRef, MUI_PALETTE, styled, uiTokens } from '../../theme'
import { IconSelector } from '../Icons'
import { Typography } from '../Typography'

const severities = ['positive', 'neutral', 'info', 'warning', 'error', 'disabled'] as const
export type StatusSeverity = (typeof severities)[number]

const severitySet = new Set<string>(severities)
export function isStatusSeverity(value: string): value is StatusSeverity {
  return severitySet.has(value)
}

export type StatusProps = {
  severity?: StatusSeverity
  children?: React.ReactNode
  icon?: string
  color?: string
  disableFallbackIcon?: boolean
}

const muiSeverityColorMap: Record<StatusSeverity, string> = {
  positive: MUI_PALETTE.success.main,
  neutral: MUI_PALETTE.text.primary,
  info: MUI_PALETTE.info.main,
  warning: MUI_PALETTE.warning.main,
  error: MUI_PALETTE.error.main,
  disabled: MUI_PALETTE.action.disabled,
}

const muiSeverityIconNameMap: Record<StatusSeverity, string> = {
  positive: 'CheckCircle',
  neutral: 'Block',
  info: 'Info',
  warning: 'Warning',
  error: 'Error',
  disabled: 'Block',
}

const MuiRefContainer = styled('span')`
  display: flex;
`

const MuiContainer = styled('div', {
  shouldForwardProp: (prop) => {
    return prop !== 'color'
  },
})<{ color: string }>`
  color: ${(props) => props.color};
  display: flex;
  align-items: center;
  gap: 4px;
`

const MuiStatusWrapper = React.forwardRef<HTMLSpanElement, StatusProps>(function MuiStatusWrapper(
  { children, severity = 'neutral', color: _color, icon, disableFallbackIcon, ...props },
  ref,
) {
  const color = _color ? _color : muiSeverityColorMap[severity]
  const iconName = icon ? icon : disableFallbackIcon ? undefined : muiSeverityIconNameMap[severity]

  return (
    <MuiRefContainer ref={ref} {...props}>
      <MuiContainer color={color}>
        {iconName && <IconSelector iconName={iconName} fontSize="small" color="inherit" />}
        <Typography variant="body1" color="inherit">
          {children}
        </Typography>
      </MuiContainer>
    </MuiRefContainer>
  )
})

const NovaRefContainer = styled('span', { shouldForwardProp: (prop) => prop !== 'color' })<{ color?: string }>`
  display: flex;
  > * {
    ${(props) => (props.color ? `color: ${props.color};` : '')}
  }
`

const NovaIconAdapter = ({ icon }: { icon: string }) => {
  return (
    <div className="Status-icon">
      <IconSelector iconName={icon} />
    </div>
  )
}

const NovaStatusWrapper = React.forwardRef<HTMLSpanElement, StatusProps>(function NovaStatusWrapper(
  { children, severity: _severity = 'neutral', color: _color, icon, disableFallbackIcon, ...props },
  ref,
) {
  const disabled = _severity === 'disabled'
  const severity = disabled || _color ? 'neutral' : _severity
  const color = disabled ? uiTokens.uiContent.disabled : _color
  const IconComponent = icon ? () => <NovaIconAdapter icon={icon} /> : disableFallbackIcon ? () => <></> : undefined

  return (
    <NovaRefContainer ref={ref} color={color} {...props}>
      <NovaStatus IconComponent={IconComponent} severity={severity}>
        {children}
      </NovaStatus>
    </NovaRefContainer>
  )
})

export const Status = createComponentWrapperWithRef(
  {
    mui: MuiStatusWrapper,
    nova: NovaStatusWrapper,
  },
  'Status',
)
